import { useEffect } from "react";
import {
    NavLink,
    Outlet,
    useNavigate,
    useLocation,
} from "react-router-dom";
import { Loader2, LogOut, LayoutDashboard, Users, Briefcase, DollarSign, ClipboardList } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LOGO_IMAGE } from "@/lib/site";

const NAV = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/prospects", label: "Prospects", icon: Users },
    { to: "/admin/applications", label: "Applications", icon: ClipboardList },
    { to: "/admin/clients", label: "Clients", icon: Briefcase },
    { to: "/admin/finances", label: "Finances", icon: DollarSign },
];

export default function AdminLayout() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        document.title = "Admin · 90 Day Alpha";
    }, []);

    useEffect(() => {
        if (loading) return;
        if (location.state?.user?.is_admin) return;
        if (!user) {
            navigate("/login", { replace: true });
        } else if (!user.is_admin) {
            navigate("/login?error=not_authorized", { replace: true });
        }
    }, [user, loading, navigate, location.state]);

    if (loading || !user) {
        return (
            <div
                data-testid="admin-loading"
                className="min-h-[100vh] electric-bg flex items-center justify-center"
            >
                <div className="flex items-center gap-3 text-white/80">
                    <Loader2 className="animate-spin text-[#00B7FF]" size={20} />
                    <span className="text-xs uppercase tracking-[0.25em] font-semibold">
                        Loading admin…
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-layout" className="min-h-screen electric-bg">
            <div className="flex flex-col lg:flex-row min-h-screen">
                <aside className="lg:w-64 lg:min-h-screen lg:border-r border-b lg:border-b-0 border-[#142142] bg-[#020617]/80 backdrop-blur-xl">
                    <div className="px-5 py-5 border-b border-[#142142] flex items-center gap-3">
                        <img
                            src={LOGO_IMAGE}
                            alt="90 Day Alpha"
                            className="h-10 w-auto drop-shadow-[0_0_15px_rgba(30,144,255,0.45)]"
                        />
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#00B7FF]">
                                Admin Console
                            </p>
                            <p className="text-white font-display font-bold text-sm">
                                Command Center
                            </p>
                        </div>
                    </div>
                    <nav className="px-3 py-4 flex lg:flex-col gap-1 overflow-x-auto">
                        {NAV.map(({ to, label, icon: Icon, end }) => (
                            <NavLink
                                key={to}
                                to={to}
                                end={end}
                                data-testid={`admin-nav-${label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                                        isActive
                                            ? "bg-[#0a1429] text-[#00B7FF] border-l-2 border-[#1E90FF]"
                                            : "text-white/65 hover:text-white border-l-2 border-transparent hover:bg-[#0a1429]/40"
                                    }`
                                }
                            >
                                <Icon size={16} />
                                <span className="uppercase tracking-[0.12em] text-xs">
                                    {label}
                                </span>
                            </NavLink>
                        ))}
                    </nav>
                    <div className="hidden lg:block lg:absolute lg:bottom-0 lg:left-0 lg:w-64 px-5 py-5 border-t border-[#142142] bg-[#020617]/90">
                        <div className="flex items-center gap-3">
                            {user?.picture ? (
                                <img
                                    src={user.picture}
                                    alt={user.name || user.email}
                                    className="w-9 h-9 rounded-full border border-[#1a2a4a]"
                                />
                            ) : (
                                <div className="w-9 h-9 rounded-full bg-[#0a1429] border border-[#1a2a4a] flex items-center justify-center text-[#00B7FF] text-xs font-bold">
                                    {(user?.name || user?.email || "?")
                                        .slice(0, 1)
                                        .toUpperCase()}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p
                                    className="text-white text-xs font-semibold truncate"
                                    data-testid="admin-user-name"
                                >
                                    {user.name || user.email}
                                </p>
                                <p className="text-white/50 text-[10px] truncate">
                                    {user.email}
                                </p>
                            </div>
                            <button
                                type="button"
                                data-testid="admin-logout"
                                onClick={async () => {
                                    await logout();
                                    navigate("/login", { replace: true });
                                }}
                                aria-label="Sign out"
                                className="text-white/55 hover:text-[#fca5a5] transition-colors"
                            >
                                <LogOut size={16} />
                            </button>
                        </div>
                    </div>
                </aside>

                <main className="flex-1 px-5 md:px-10 py-8 md:py-12">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
